import React from "react";
import { ChevronRight } from "lucide-react";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-white text-[26px] font-semibold tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="text-white/55 text-[13px] mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}

export function StatCard({ label, value, sub, accent = "teal", icon }) {
  const colors = {
    teal: "text-[#67e8d1]",
    rose: "text-rose-300",
    amber: "text-amber-300",
    sky: "text-sky-300",
    white: "text-white",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-start justify-between">
        <span className="text-white/60 text-[11px] uppercase tracking-wider">
          {label}
        </span>
        {icon && <span className={colors[accent]}>{icon}</span>}
      </div>
      <div className="mt-2 text-white text-[28px] leading-none font-semibold tracking-tight">
        {value}
      </div>
      {sub && <div className="mt-1 text-white/55 text-[12px]">{sub}</div>}
    </div>
  );
}

export function Badge({ children, color = "teal" }) {
  const colors = {
    teal: "bg-[#14c2a1]/15 text-[#67e8d1] border-[#14c2a1]/30",
    rose: "bg-rose-400/15 text-rose-200 border-rose-400/30",
    amber: "bg-amber-400/15 text-amber-200 border-amber-400/30",
    sky: "bg-sky-400/15 text-sky-200 border-sky-400/30",
    slate: "bg-white/5 text-white/75 border-white/15",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${colors[color] || colors.slate}`}
    >
      {children}
    </span>
  );
}

export function Panel({ title, right, children, className = "" }) {
  return (
    <div
      className={`rounded-xl border border-white/10 bg-white/[0.03] ${className}`}
    >
      {(title || right) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <div className="text-white text-[14px] font-semibold">{title}</div>
          <div>{right}</div>
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Table({ columns, rows, onRowClick, empty = "Nessun dato" }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.03]">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-white/60 text-[11px] uppercase tracking-wider">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className="text-left font-medium px-4 py-2.5 whitespace-nowrap"
                style={{ width: c.width }}
              >
                {c.label}
              </th>
            ))}
            {onRowClick && <th className="w-8" />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (onRowClick ? 1 : 0)}
                className="px-4 py-10 text-center text-white/50"
              >
                {empty}
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              onClick={() => onRowClick && onRowClick(r)}
              className={`border-t border-white/5 ${onRowClick ? "cursor-pointer hover:bg-white/5" : ""}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-2.5 text-white/85 whitespace-nowrap">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
              {onRowClick && (
                <td className="pr-3">
                  <ChevronRight size={14} className="text-white/40" />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TextInput({ icon, ...props }) {
  return (
    <div className="inline-flex items-center h-9 px-3 gap-2 rounded-lg bg-white/5 border border-white/10 focus-within:border-[#14c2a1]">
      {icon && <span className="text-white/50">{icon}</span>}
      <input
        {...props}
        className="bg-transparent text-white text-[13px] focus:outline-none w-full"
      />
    </div>
  );
}

export function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
    >
      {options.map((o) => (
        <option
          key={o.value}
          value={o.value}
          className="bg-[#0d1519] text-white"
        >
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Button({ children, variant = "primary", size = "md", ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all disabled:opacity-60";
  const sizes = {
    md: "h-9 px-3 text-[13px]",
    sm: "h-8 px-2.5 text-[12px]",
    lg: "h-11 px-4 text-[14px]",
  };
  const variants = {
    primary:
      "text-white",
    ghost: "bg-white/5 border border-white/10 hover:bg-white/10 text-white",
    danger: "bg-rose-500/15 border border-rose-400/30 text-rose-200 hover:bg-rose-500/25",
    success: "bg-[#14c2a1]/15 border border-[#14c2a1]/30 text-[#67e8d1] hover:bg-[#14c2a1]/25",
  };
  const styleExtra =
    variant === "primary"
      ? {
          background: "linear-gradient(155deg,#12b096,#0a8670)",
          boxShadow: "0 8px 20px -12px rgba(20,194,161,0.55)",
        }
      : undefined;
  return (
    <button
      {...rest}
      style={styleExtra}
      className={`${base} ${sizes[size]} ${variants[variant]}`}
    >
      {children}
    </button>
  );
}
